import React, { memo, useMemo } from 'react';

import { Button, Icons, SpinnerOverlay } from '@components/common';

import { useAlarmDetails } from '@hooks/alarms/alarmsByVendor/useAlarmDetails';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TNameTitle } from '@interfaces/common/common';

import AlarmDetailsField from './AlarmDetailsField';
import { getAlarmDetails } from './utils';

import styles from './AlarmDetails.module.scss';

interface IAlarmDetailsProps {
	id: string;
	vendor: TNameTitle;
	isHistory?: boolean;
	onClear: () => void;
	onAcknowledge: () => void;
	onClose: () => void;
}

const AlarmDetails = ({
	id,
	vendor,
	isHistory,
	onClear,
	onAcknowledge,
	onClose,
}: IAlarmDetailsProps) => {
	const { alarm, isLoadingAlarm } = useAlarmDetails(vendor?.name, id, isHistory);
	const { user } = useAppSelector(auth);
	const userName = `${user?.firstName} ${user?.lastName}`;
	const isEditAvailable = userName === alarm?.acknowledgedBy && alarm?.isAcknowledged;
	const editNotes  = useMemo(() => isEditAvailable ? onAcknowledge : () => null, [
		isEditAvailable, onAcknowledge,
	]);

	const { fields = [], isClear = false, isAcknowledge = false } = useMemo(() => {
		return getAlarmDetails({ vendor: vendor?.name, alarm, editNotes });
	}, [vendor?.name, alarm, editNotes]);

	return (
		<div>
			{isLoadingAlarm && <SpinnerOverlay />}
			{
				fields.map((field) => (
					<AlarmDetailsField
						key={field.label}
						{...field}
					/>
				))
			}
			<div className={styles.buttonsWrapper}>
				{isClear && (
					<Button
						className={styles.button}
						icon={Icons.clearDataIcon()}
						onClick={onClear}
						text='Clear alarm'
						withoutBackground
					/>
				)}
				{isAcknowledge && (
					<Button
						className={styles.button}
						icon={Icons.thumbsUp()}
						onClick={onAcknowledge}
						text='Mark as acknowledged'
						withoutBackground
					/>
				)}
				{!isClear && !isAcknowledge && (
					<Button
						className={styles.button}
						onClick={onClose}
						text='Cancel'
					/>
				)}
			</div>
		</div>
	);
};

export default memo(AlarmDetails);
